import React, { PropsWithChildren, useEffect } from 'react';

import { ReactComponent as Close } from '/src/images/icons/close.svg';

import useGetMobileAppWebViewHeader from '../../../hooks/use-get-mobile-app-web-view';
import { CreatePortal } from '../create-portal/create-portal';

interface SideModalDisplayProps {
  handleClose: () => void;
  closable: boolean;
  showBackdrop: boolean;
}

export const SideModalDisplay = ({
  handleClose,
  closable,
  showBackdrop,
  children,
}: PropsWithChildren<SideModalDisplayProps>) => {
  const isWebView = useGetMobileAppWebViewHeader();

  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (closable) {
      document.body.addEventListener('keydown', closeWithEscape);
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
      document.body.removeEventListener('keydown', closeWithEscape);
    };
  }, [handleClose, closable]);

  const backdropCssClasses = `fixed inset-0 overflow-hidden z-[50] flex flex-col justify-center items-center${
    showBackdrop ? ' bg-transparency-backdrop' : ''
  }`;

  return (
    <CreatePortal>
      <div
        id="side-modal"
        className={backdropCssClasses}
        onClick={() => (closable ? handleClose() : null)}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          id="side-modal-content"
          className={
            'scrollbar-livat-hidden fixed right-0 top-0 z-[50] ml-auto h-full w-full animate-fadeInUp overflow-y-auto bg-background-primary-light transition-all duration-1000 ease-in-out sm:max-w-[484px] sm:animate-fadeInRight sm:rounded-es-xl sm:rounded-ss-xl xl:max-w-[580px]'
          }
        >
          {closable && !isWebView && (
            <button
              className={
                'absolute right-4 top-4 grid h-12 w-12 place-items-center content-center rounded-full bg-neutral-grey-100 hover:bg-neutral-grey-200 focus:bg-accent-primary-dark active:bg-accent-primary-dark lg:right-4 lg:top-4'
              }
              onClick={() => handleClose()}
            >
              <Close className={'h-3 w-3 lg:h-3 lg:w-3'} />
            </button>
          )}
          {children}
        </div>
      </div>
    </CreatePortal>
  );
};
